import { AppData, Lightning } from '@lightningjs/sdk';
import SupportSection from 'components/pages/settings/sections/SupportSection';
import PlaybackSettingsSection from 'components/pages/settings/sections/PlaybackSettingsSection';
import TextSection from 'components/pages/settings/sections/TextSection';
import ToggleSettingSection from 'components/pages/settings/sections/toggleSection/ToggleSettingSection';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { translate } from 'support/translate';
import { Platform } from 'models/platforms/platform';
import ParentalControlsSection from 'components/pages/settings/sections/toggleSection/ParentalControlsSection';
import PrivacyLegalSection from './sections/PrivacyLegalSection';
import DataCenterSection from './sections/DataCenterSection';
import AccountSection from './sections/AccountSection';

export interface SettingsSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  selection: number;
  isLoggedIn: boolean;

  Sections: {
    Account: typeof TextSection;
    PlaybackSettings: typeof TextSection;
    PrivacyLegal: typeof TextSection;
    DataCenter: typeof TextSection;
    Support: typeof SupportSection;
    ShareHistoryWithPlatform: typeof ToggleSettingSection;
    ParentalControls: typeof ParentalControlsSection;
  };
}

export const LOGGED_IN_SECTION: keyof SettingsSectionTemplateSpec['Sections'] =
  'Account';

export const SETTING_SECTIONS: Array<
  keyof SettingsSectionTemplateSpec['Sections']
> = ['PlaybackSettings', 'PrivacyLegal', 'DataCenter', 'Support'];

export const TIZEN_SETTINGS_SECTION: typeof SETTING_SECTIONS = [
  ...SETTING_SECTIONS,
  'ShareHistoryWithPlatform',
];

export const LG_SETTINGS_SECTION: typeof SETTING_SECTIONS = [
  ...SETTING_SECTIONS,
  'ParentalControls',
];

export const UNKNOWN_SETTINGS_SECTION: typeof SETTING_SECTIONS = [
  ...LG_SETTINGS_SECTION,
];

export const SETTING_SECTION_WIDTH = 802;
export const SETTING_SECTION_HEIGHT = 872;

export default class SettingsSection
  extends HoverableComponent<SettingsSectionTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<SettingsSectionTemplateSpec>
{
  private _Sections = this.getByRef('Sections')!;
  private _selectionIndex = 0;
  static _isLoggedIn: boolean;

  set selection(selection: number) {
    this.hideAllSections();
    this.getSectionFromIndex(selection)?.patch({
      visible: true,
    });
    this._selectionIndex = selection;
  }

  get selection() {
    return this._selectionIndex;
  }

  // TODO: Remove this setter and directly check for logged in once implemented.
  set isLoggedIn(value: boolean) {
    SettingsSection._isLoggedIn = value;
  }

  get isLoggedIn() {
    return SettingsSection._isLoggedIn;
  }

  static override _template() {
    return {
      Sections: {
        h: SETTING_SECTION_HEIGHT,
        w: SETTING_SECTION_WIDTH,
        clipping: true, // TODO: Added for testing purpose, remove after page implementations
        Account: {
          type: AccountSection,
          sectionId: 'Account',
          visible: false,
        },
        PlaybackSettings: {
          type: PlaybackSettingsSection,
          sectionId: 'PlaybackSettings',
          visible: false,
        },
        PrivacyLegal: {
          type: PrivacyLegalSection,
          sectionId: 'PrivacyLegal',
          visible: false,
        },
        DataCenter: {
          type: DataCenterSection,
          sectionId: 'DataCenter',
          visible: false,
        },
        Support: {
          type: SupportSection,
          visible: false,
        },
        ShareHistoryWithPlatform: {
          type: ToggleSettingSection,
          visible: false,
          getToggleState: this.shouldShareHistoryWithPlatform,
          label: translate(
            'settings.ShareHistoryWithPlatform.label',
            this.shareHistoryPlatformName(),
          ),
          description: translate(
            'settings.ShareHistoryWithPlatform.description',
            this.shareHistoryPlatformName(),
          ),
          signals: {
            onChanged: '_onShareHistoryWithPlatformChanged',
          },
        },
        ParentalControls: {
          type: ParentalControlsSection,
          visible: false,
        },
      },
    };
  }

  override _getFocused() {
    return (this.getSectionFromIndex(this._selectionIndex) ??
      this._Sections) as Lightning.Component;
  }

  static getSettingsSections() {
    const platform = AppData?.device.getPlatform();
    let sections;
    switch (platform) {
      case Platform.LG:
        sections = LG_SETTINGS_SECTION;
        break;
      case Platform.TIZEN:
        sections = TIZEN_SETTINGS_SECTION;
        break;
      case Platform.UNKNOWN:
        sections = UNKNOWN_SETTINGS_SECTION;
        break;
      case Platform.VIZIO:
      case Platform.COMCAST:
      default:
        sections = SETTING_SECTIONS;
    }

    return [...(this._isLoggedIn ? [LOGGED_IN_SECTION] : []), ...sections];
  }

  private static shouldShareHistoryWithPlatform() {
    return AppData!.historyService.shouldSharePlatformHistory();
  }

  private static shareHistoryPlatformName() {
    return AppData!.device.shareHistoryPlatformName();
  }

  private getSectionFromIndex(index: number) {
    const settingsSections = SettingsSection.getSettingsSections();
    const section = settingsSections[index];
    if (section === undefined) return undefined;

    return this._Sections.getByRef(settingsSections[index]!);
  }

  private hideAllSections() {
    const sections: ReadonlyArray<
      keyof SettingsSectionTemplateSpec['Sections']
    > = [
      LOGGED_IN_SECTION,
      ...SETTING_SECTIONS,
      ...TIZEN_SETTINGS_SECTION,
      ...LG_SETTINGS_SECTION,
      ...UNKNOWN_SETTINGS_SECTION,
    ];

    for (const section of sections) {
      this._Sections.getByRef(section)?.patch({ visible: false });
    }
  }

  private _onShareHistoryWithPlatformChanged(share: boolean) {
    AppData!.historyService.onShareHistoryWithPlatformChanged(share);
  }
}
